import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  CheckCircle2, 
  Circle, 
  ChevronDown, 
  ChevronUp,
  Sparkles,
  Target,
  Calendar,
  UserCircle,
  MessageSquare,
  TrendingUp,
  X
} from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { wellnessService } from "@/services/wellnessService";
import { goalsService } from "@/services/goalsService";
import { calendarService } from "@/services/calendarService";

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  action: string;
  icon: React.ReactNode;
  completed: boolean;
  onClick: () => void;
}

export default function GettingStartedChecklist() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);

  // Initialize checklist items
  useEffect(() => {
    if (!user?.id) return;

    const loadChecklistStatus = async () => {
      // Check localStorage for dismissed state
      const dismissed = localStorage.getItem(`checklist_dismissed_${user.id}`);
      if (dismissed === "true") {
        setIsDismissed(true);
        return;
      }

      try {
        // Parallel fetch all data from API
        // Note: apiService.get() returns data directly (unwrapped from { success, data })
        const [statsData, calendarData, goalsData] = await Promise.all([
          // Get check-ins count from stats
          wellnessService.getStats().catch(() => ({ totalCheckIns: 0 })),

          // Get calendar integrations
          calendarService.getIntegrations().catch(() => []),

          // Get goals
          goalsService.getGoals().catch(() => [])
        ]);

        // Load completion status from API responses
        const totalCheckIns = statsData?.totalCheckIns || 0;
        const checkIns = totalCheckIns > 0 ? [{ id: 'has-checkins' }] : [];

        // Profile data from user context
        const hasAvatar = !!(user?.avatarUrl);

        // Calendar integrations
        const calendarIntegrations = Array.isArray(calendarData) ? calendarData : [];
        const hasCalendar = calendarIntegrations.length > 0;

        // Goals
        const goals = Array.isArray(goalsData) ? goalsData : (goalsData?.goals || []);

        if (process.env.NODE_ENV === 'development') {
          console.log('GettingStartedChecklist - Profile completion check:', {
            hasAvatar,
            hasCalendar,
            avatarUrl: user?.avatarUrl,
            calendarCount: calendarIntegrations.length,
            totalCheckIns,
            goalsCount: goals.length
          });
        }

        const items: ChecklistItem[] = [
          {
            id: "complete-profile",
            title: "Complete Your Profile",
            description: (() => {
              const missing = [];
              if (!hasAvatar) missing.push("photo");
              if (!hasCalendar) missing.push("calendar");
              
              if (missing.length === 0) return "Profile setup complete";
              if (missing.length === 2) return "Add photo and connect calendar";
              return `Add ${missing[0]}`;
            })(),
            action: "Go to Profile",
            icon: <UserCircle className="h-4 w-4" />,
            completed: !!(hasAvatar && hasCalendar),
            onClick: () => navigate("/profile"),
          },
          {
            id: "first-checkin",
            title: "Complete Your First Check-in",
            description: "Track your mood and energy to start building insights",
            action: "Start Check-in",
            icon: <Calendar className="h-4 w-4" />,
            completed: !!(checkIns && checkIns.length > 0),
            onClick: () => navigate("/check-in"),
          },
          {
            id: "set-goal",
            title: "Set Your First Goal",
            description: "Define what you want to achieve and track your progress",
            action: "Create Goal",
            icon: <Target className="h-4 w-4" />,
            completed: !!(goals && goals.length > 0),
            onClick: () => navigate("/profile", { state: { activeTab: "growth" } }),
          },
          {
            id: "explore-insights",
            title: "Explore AI Insights",
            description: "After 5 check-ins, unlock personalized patterns and insights",
            action: "View Insights",
            icon: <TrendingUp className="h-4 w-4" />,
            completed: totalCheckIns >= 5,
            onClick: () => navigate("/patterns"),
          },
          {
            id: "connect-team",
            title: "Connect with Others (Optional)",
            description: "Join a team or explore collaboration features when ready",
            action: "Explore Teams",
            icon: <MessageSquare className="h-4 w-4" />,
            completed: !!user.teamId,
            onClick: () => navigate("/team"),
          },
        ];

        setChecklist(items);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Error loading checklist status:", error);
        }
      }
    };

    loadChecklistStatus();
  }, [user, navigate]);

  const completedCount = checklist.filter(item => item.completed).length;
  const progress = checklist.length > 0 ? (completedCount / checklist.length) * 100 : 0;

  const handleDismiss = () => {
    if (user?.id) {
      localStorage.setItem(`checklist_dismissed_${user.id}`, "true");
      setIsDismissed(true);
    }
  };

  // Don't show if dismissed or all items completed
  if (isDismissed || completedCount === checklist.length) {
    return null;
  }

  // Don't show for experienced users (more than 7 days old)
  if (user?.joinedDate) {
    const daysSinceJoined = Math.floor(
      (new Date().getTime() - new Date(user.joinedDate).getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysSinceJoined > 7) {
      return null;
    }
  }

  return (
    <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-full">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Getting Started</CardTitle>
              <CardDescription className="text-xs">
                Complete these steps to get the most out of Step
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleDismiss}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Progress bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{completedCount} of {checklist.length} completed</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Checklist items */}
        {isExpanded && (
          <div className="space-y-2">
            {checklist.map((item) => (
              <div
                key={item.id}
                className={cn(
                  "flex items-start gap-3 p-3 rounded-lg border transition-colors",
                  item.completed
                    ? "bg-muted/50 border-muted"
                    : "bg-background hover:bg-muted/50 border-border cursor-pointer"
                )}
                onClick={!item.completed ? item.onClick : undefined}
              >
                <div className="mt-0.5">
                  {item.completed ? (
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      "text-sm font-medium",
                      item.completed && "line-through text-muted-foreground"
                    )}>
                      {item.title}
                    </span>
                    {item.icon}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {item.description}
                  </p>
                  {!item.completed && (
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        item.onClick();
                      }}
                    >
                      {item.action} →
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Completion message */}
        {completedCount === checklist.length - 1 && (
          <div className="text-center py-2">
            <p className="text-sm text-muted-foreground">
              🎉 Almost there! Complete the last step to finish your setup.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}